import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, BarChart3, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  ['users_scheduled', 'Scheduled'], ['users_purged', 'Purged'], ['users_restored', 'Restored'],
  ['restore_requests_approved', 'Requests approved'], ['restore_requests_declined', 'Requests declined'],
  ['restore_requests_received', 'Requests received'], ['renewals_stopped', 'Renewals stopped'],
  ['stripe_issues', 'Stripe issues'], ['inactive_warnings_sent', 'Inactive warnings'], ['bulk_batches', 'Bulk batches'],
  ['pending_at_report_time', 'Pending now'],
];

// Current month (partial) plus the previous 23
const MONTHS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - i);
  const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) + (i === 0 ? ' (so far)' : '');
  return { value, label };
});

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const CleanupReportCard = () => {
  const [month, setMonth] = useState(lastMonth());
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!month) return;
    setReport(null);
    axios.get(`${API}/admin/cleanup-report`, { params: { month }, withCredentials: true })
      .then((res) => setReport(res.data))
      .catch(() => toast.error('Could not load cleanup report'));
  }, [month]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/cleanup-report/pdf`, { params: { month }, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cleanup-report-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="mb-4" data-testid="cleanup-report-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-4 h-4" /> Monthly cleanup report</CardTitle>
          <CardDescription>Emailed to all admins on the 1st. Pick any month to view or download it.</CardDescription>
        </div>
        <div className="flex gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-48" data-testid="cleanup-report-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`cleanup-month-${m.value}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={download} disabled={downloading || !month} data-testid="cleanup-report-pdf-btn">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1.5" />PDF</>}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!report ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
            {METRICS.map(([key, label]) => (
              <div key={key} className={`rounded-lg border p-3 ${key === 'stripe_issues' && report[key] > 0 ? 'border-red-200 bg-red-50' : 'bg-slate-50'}`}
                data-testid={`cleanup-metric-${key}`}>
                <p className="text-2xl font-bold tabular-nums">{report[key]}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CleanupReportCard;
